import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Table, Tag} from 'antd'
import {getOutputGlossary} from '../../../../services/api/apiProductionPlanning'
import {ROUTES} from '../../constants'
import {
    FILE_DATA_FIELDS,
    FILE_DATA_PAYLOAD_SAMPLE,
    OUTPUT_AREA_WARNING,
    OUTPUT_CLICK_MAP,
    OUTPUT_INFO_BOXES,
    OUTPUT_METRICS,
} from '../../constants/glossary'
import Notice from '../shared/Notice'
import {CountUnitTag, TimeBasisTag} from '../shared/StageBadges'

/**
 * SCR-M18-004 — Giải thích chỉ số.
 *
 * Một chỗ duy nhất giải thích mọi con số trên báo cáo.
 *
 * ⚠️ §E.2: nội dung bảng 1 ở đây và chú thích rê chuột ở SCR-M18-001 phải lấy từ **cùng một nguồn**
 *    (`constant/glossary.js`, và về sau là `GET /kpi/output/glossary`). Viết tay ở hai nơi thì sau
 *    vài lần sửa sẽ lệch nhau, và người dùng sẽ tin vào cái sai.
 */
/** `embedded` = đang nằm trong modal của màn báo cáo — xem ghi chú ở `SuspectPage`. */
function GlossaryPage({history, embedded, onClose}) {
    const [metrics, setMetrics] = useState(OUTPUT_METRICS)
    const [stages, setStages] = useState([])

    useEffect(() => {
        getOutputGlossary()
            .then((data) => {
                // Bản dự phòng trong constant chỉ dùng khi service chưa trả nội dung.
                if (data && data.metrics) setMetrics(data.metrics)
                if (data && data.stages) setStages(data.stages)
            })
            .catch(() => undefined)
    }, [])

    return (
        <div className={embedded ? 'VcEmbeddedPage' : 'VcPage VcGlossaryPage container-fluid'}>
            <div className="VcPageHead">
                <h1 className="VcPageTitle">Giải thích chỉ số</h1>
                <div className="VcPageActions">
                    {embedded ? (
                        <Button onClick={onClose}>Đóng</Button>
                    ) : (
                        <Button onClick={() => history.push(ROUTES.OUTPUT_REPORT)}>← Quay lại báo cáo</Button>
                    )}
                </div>
            </div>

            <Notice level="info">
                Điểm dễ hiểu sai nhất của báo cáo này: <strong>Đơn vị đếm</strong> và{' '}
                <strong>Cách tính giờ</strong> là hai việc tách rời. Với In, Cắt, Khắc thì sản lượng đếm
                theo <strong>sản phẩm</strong>, còn giờ định mức cộng theo <strong>từng dòng vật liệu</strong>
                {' '}của sản phẩm đó. Đổi cách tính giờ mà giữ đơn vị đếm thì cột Sản lượng không đổi, chỉ
                cột Giờ định mức đổi.
            </Notice>

            <h3 className="VcSectionTitle">Bảng 1 — Các chỉ số trên báo cáo</h3>
            <Table
                className="VcTable"
                rowKey="key"
                size="small"
                pagination={false}
                dataSource={metrics}
                columns={[
                    {title: 'Chỉ số', dataIndex: 'label', width: 200},
                    {
                        title: 'Nghĩa',
                        dataIndex: 'meaning',
                        // E-AC-03: chỉ số so sánh phải ghi rõ chiều tốt xấu — nhưng `meaning` của
                        // nhiều dòng ĐÃ chứa sẵn câu đó (xem `constants/glossary.js:48` so với `:51`),
                        // nối `direction` vào nữa là in lặp: "…Càng thấp càng tốt. Càng thấp càng tốt."
                        // Chỉ nối khi `meaning` chưa nói.
                        render: (value, row) => {
                            const text = value || ''
                            const said = row.direction
                                && text.toLowerCase().indexOf(row.direction.toLowerCase()) !== -1

                            return (
                                <span>
                                    {text}
                                    {row.direction && !said && <strong> {row.direction}.</strong>}
                                </span>
                            )
                        },
                    },
                    {title: 'Cách tính', dataIndex: 'formula'},
                    {
                        title: 'Bấm được',
                        dataIndex: 'clickable',
                        width: 100,
                        render: (clickable) => (clickable
                            ? <Tag color="blue">Bấm được</Tag>
                            : <Tag>Không bấm</Tag>),
                    },
                ]}
            />

            <h3 className="VcSectionTitle">Bảng 2 — Chín công đoạn của dòng decor</h3>
            <Table
                className="VcTable"
                rowKey="stageCode"
                size="small"
                pagination={false}
                dataSource={stages}
                columns={[
                    {title: '#', dataIndex: 'sortOrder', width: 44},
                    {title: 'Công đoạn', dataIndex: 'name', width: 170},
                    {
                        title: 'Đơn vị đếm',
                        dataIndex: 'countUnit',
                        width: 130,
                        render: (countUnit) => <CountUnitTag countUnit={countUnit} variant="m18"/>,
                    },
                    {
                        title: 'Cách tính giờ',
                        key: 'timeBasis',
                        width: 200,
                        render: (text, row) => (
                            <TimeBasisTag timeBasis={row.timeBasis} detectField={row.detectField} variant="m18"/>
                        ),
                    },
                    {
                        title: 'Đại lượng',
                        dataIndex: 'detectField',
                        render: (detectField, row) => {
                            if (!detectField) return <span className="VcMuted">không dùng dòng vật liệu</span>

                            // E-AC-06: ghi rõ Khắc dùng CHUNG trường area với In.
                            const shared = row.stageCode === 'khac' ? ' — dùng chung với công đoạn In' : ''

                            return <span><code>{detectField}</code>{shared}</span>
                        },
                    },
                ]}
            />

            <Notice level="warning">{OUTPUT_AREA_WARNING}</Notice>

            <h3 className="VcSectionTitle">Dữ liệu file in nhận từ FFM</h3>
            <pre className="VcCodeBlock">{FILE_DATA_PAYLOAD_SAMPLE}</pre>
            <Table
                className="VcTable"
                rowKey="field"
                size="small"
                pagination={false}
                dataSource={FILE_DATA_FIELDS}
                columns={[
                    {title: 'Trường', dataIndex: 'field', width: 180, render: (value) => <code>{value}</code>},
                    {title: 'Ý nghĩa', dataIndex: 'meaning'},
                    {title: 'Công đoạn dùng', dataIndex: 'usedBy', width: 200},
                ]}
            />

            <h3 className="VcSectionTitle">Bảng 3 — Bấm vào số nào thì ra danh sách gì</h3>
            <Table
                className="VcTable"
                rowKey="cell"
                size="small"
                pagination={false}
                dataSource={OUTPUT_CLICK_MAP}
                rowClassName={(row) => {
                    if (row.tone === 'warning') return 'VcRow--noStandard'
                    if (row.tone === 'danger') return 'VcRow--noFileData'

                    return ''
                }}
                columns={[
                    {title: 'Ô bấm được', dataIndex: 'cell', width: 320},
                    {title: 'Danh sách mở ra', dataIndex: 'opens'},
                ]}
            />

            {OUTPUT_INFO_BOXES.map((text, index) => (
                <Notice key={index} level="info">{text}</Notice>
            ))}
        </div>
    )
}

GlossaryPage.propTypes = {
    embedded: PropTypes.bool,
    onClose: PropTypes.func,
}

GlossaryPage.defaultProps = {embedded: false, onClose: () => undefined}

export default GlossaryPage
